import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { serialApi, SerialItem } from '@/api/serial.api';
import { filterItems } from '@/api/item.api';
import AppLayout from '@/components/feature/AppLayout';

interface GroupedItemSerials {
  itemId: string;
  itemName: string;
  itemCode?: string;
  serials: SerialItem[];
  total: number;
  inStock: number;
  sold: number;
  other: number;
}

export default function SerialsPage() {
  const navigate = useNavigate();

  // Tab State
  const [activeTab, setActiveTab] = useState<'master' | 'warranty'>('master');

  // Master List State
  const [serials, setSerials] = useState<SerialItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [conditionFilter, setConditionFilter] = useState('ALL');
  const [selectedItemId, setSelectedItemId] = useState('ALL');
  const [itemsList, setItemsList] = useState<{ id: string; name: string; code?: string; isSerialTracked?: boolean }[]>([]);
  const [viewMode, setViewMode] = useState<'grouped' | 'flat'>('grouped');
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Warranty Tracking State
  const [warrantySerials, setWarrantySerials] = useState<SerialItem[]>([]);
  const [warrantyLoading, setWarrantyLoading] = useState(false);
  const [warrantyDays, setWarrantyDays] = useState(90);

  // Warranty Extension Modal State
  const [extendModalSerial, setExtendModalSerial] = useState<SerialItem | null>(null);
  const [newWarrantyDate, setNewWarrantyDate] = useState('');
  const [extendSaving, setExtendSaving] = useState(false);

  // Fetch items list on mount for the item dropdown filter
  useEffect(() => {
    async function loadItems() {
      try {
        const res = await filterItems({ isActive: 'true' });
        if (res.success && Array.isArray(res.data)) {
          const mapped = res.data
            .map((item: any) => ({
              id: item.id,
              name: item.name,
              code: item.code,
              isSerialTracked: item.is_serial_tracked ?? item.isSerialTracked,
            }))
            .sort((a, b) => a.name.localeCompare(b.name));
          setItemsList(mapped);
        }
      } catch (err) {
        console.error('Failed to load items list:', err);
      }
    }
    loadItems();
  }, []);

  // Load Master List
  useEffect(() => {
    if (activeTab === 'master') {
      loadMasterSerials();
    }
  }, [activeTab, page, statusFilter, conditionFilter, selectedItemId]);

  // Load Warranty Tab
  useEffect(() => {
    if (activeTab === 'warranty') {
      loadWarrantySerials();
    }
  }, [activeTab, warrantyDays]);

  const loadMasterSerials = async () => {
    try {
      setLoading(true);
      const res = await serialApi.list({
        status: statusFilter !== 'ALL' ? statusFilter : undefined,
        condition: conditionFilter !== 'ALL' ? conditionFilter : undefined,
        itemId: selectedItemId !== 'ALL' ? selectedItemId : undefined,
        search: search.trim() || undefined,
        page,
        limit: 50,
      });
      if (res.success) {
        setSerials(res.data);
        setTotalPages(res.pagination.totalPages || 1);
        setTotalCount(res.pagination.total || 0);

        // Default to collapse all state
        if (selectedItemId !== 'ALL') {
          setExpandedItems({ [selectedItemId]: true });
        } else {
          setExpandedItems({});
        }
      }
    } catch (err: any) {
      console.error('Failed to load serials:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadWarrantySerials = async () => {
    try {
      setWarrantyLoading(true);
      const res = await serialApi.getWarrantyExpiring(warrantyDays);
      if (res.success) {
        setWarrantySerials(res.data || []);
      }
    } catch (err: any) {
      console.error('Failed to load warranty serials:', err);
    } finally {
      setWarrantyLoading(false);
    }
  };

  // Group serials by item for arranged dropdown view
  const groupedSerials = useMemo(() => {
    const map = new Map<string, GroupedItemSerials>();
    serials.forEach((s) => {
      const key = s.item_id || s.item_name || 'unknown';
      if (!map.has(key)) {
        map.set(key, {
          itemId: key,
          itemName: s.item_name || 'Unnamed Item',
          itemCode: s.item_code,
          serials: [],
          total: 0,
          inStock: 0,
          sold: 0,
          other: 0,
        });
      }
      const group = map.get(key)!;
      group.serials.push(s);
      group.total += 1;
      if (s.status === 'IN_STOCK') group.inStock += 1;
      else if (s.status === 'SOLD') group.sold += 1;
      else group.other += 1;
    });
    return Array.from(map.values());
  }, [serials]);

  const toggleItem = (itemId: string) => {
    setExpandedItems((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };

  const toggleAll = (expand: boolean) => {
    const updated: Record<string, boolean> = {};
    groupedSerials.forEach((g) => {
      updated[g.itemId] = expand;
    });
    setExpandedItems(updated);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    loadMasterSerials();
  };

  const handleOpenExtendModal = (serial: SerialItem) => {
    setExtendModalSerial(serial);
    setNewWarrantyDate(serial.warranty_expiry_date || '');
  };

  const handleSaveExtendedWarranty = async () => {
    if (!extendModalSerial || !newWarrantyDate) return;
    try {
      setExtendSaving(true);
      await serialApi.updateStatus(extendModalSerial.id, {
        notes: `Warranty extended till ${newWarrantyDate}`,
      });
      setExtendModalSerial(null);
      if (activeTab === 'warranty') loadWarrantySerials();
      else loadMasterSerials();
    } catch (err: any) {
      alert(`Error updating warranty: ${err.message}`);
    } finally {
      setExtendSaving(false);
    }
  };

  const formatWarrantyDate = (dateStr?: string | null) => {
    if (!dateStr || dateStr === '—') return '—';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'IN_STOCK':
        return <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">IN STOCK ✅</span>;
      case 'SOLD':
        return <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">SOLD 🔵</span>;
      case 'RETURNED':
        return <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">RETURNED 🔄</span>;
      case 'DEFECTIVE':
        return <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-200">DEFECTIVE ⚠️</span>;
      case 'IN_REPAIR':
        return <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-purple-50 text-purple-700 border border-purple-200">IN REPAIR 🔧</span>;
      case 'ON_HOLD':
        return <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-yellow-50 text-yellow-700 border border-yellow-200">ON HOLD ⏸️</span>;
      case 'SCRAPPED':
        return <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 border border-gray-300">SCRAPPED ❌</span>;
      default:
        return <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-50 text-gray-600">{status}</span>;
    }
  };

  // Render a serial table row
  const renderSerialRow = (s: SerialItem, showItemCol = true) => (
    <tr
      key={s.id}
      onClick={() => navigate(`/inventory/serials/${s.id}`)}
      className="hover:bg-blue-50/40 cursor-pointer transition-colors group"
    >
      <td className="py-3 px-4 font-mono font-bold text-gray-900 flex items-center gap-1.5">
        <i className="ri-qr-code-line text-gray-400 group-hover:text-blue-600"></i>
        {s.serial_number}
      </td>
      <td className="py-3 px-4 text-gray-600 font-mono">
        {s.vendor_serial_number || s.imei || '—'}
      </td>
      {showItemCol && (
        <td className="py-3 px-4 font-medium text-gray-800">
          {s.item_name}
          {s.item_code ? <span className="text-gray-400 block text-[11px]">{s.item_code}</span> : null}
        </td>
      )}
      <td className="py-3 px-4">{getStatusBadge(s.status)}</td>
      <td className="py-3 px-4">
        <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${
          s.condition === 'GOOD'
            ? 'bg-blue-50 text-blue-700'
            : 'bg-amber-50 text-amber-700 border border-amber-200'
        }`}>
          {s.condition}
        </span>
      </td>
      <td className="py-3 px-4 text-gray-600">
        {s.status === 'SOLD' ? (
          <span className="flex items-center gap-1 text-blue-700 font-medium">
            <i className="ri-user-line text-[11px]"></i> {s.customer_name || 'Customer'}
          </span>
        ) : (
          <span className="flex items-center gap-1">
            <i className="ri-building-line text-[11px] text-gray-400"></i> {s.warehouse_name || 'Warehouse'}
          </span>
        )}
      </td>
      <td className="py-3 px-4 text-gray-600 font-medium">
        {formatWarrantyDate(s.warranty_expiry_date)}
      </td>
      <td className="py-3 px-4 text-center" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          onClick={() => navigate(`/inventory/serials/${s.id}`)}
          className="px-2.5 py-1 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors font-medium"
        >
          View History
        </button>
      </td>
    </tr>
  );

  return (
    <AppLayout>
      <div className="p-6 max-w-7xl mx-auto space-y-6 animate-in fade-in duration-300">
        {/* Top Banner */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <div>
            <div className="flex items-center gap-2">
              <span className="p-2 bg-gradient-to-tr from-blue-600 to-indigo-600 text-white rounded-xl shadow-sm">
                <i className="ri-qr-code-line text-xl"></i>
              </span>
              <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Serial Number Tracking</h1>
            </div>
            <p className="text-sm text-gray-500 mt-1">
              Track individual unit-level lifecycle, IMEIs, vendor service tags, warranties, and movement histories.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate('/inventory/serial-tracking')}
              className="px-4 py-2.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-xl text-xs font-semibold flex items-center gap-1.5 transition-colors"
            >
              <i className="ri-route-line text-sm"></i> Trace Journey
            </button>
          </div>
        </div>

        {/* Main Tabs */}
        <div className="flex gap-4 border-b border-gray-200 px-2 bg-white rounded-xl shadow-sm p-1.5">
          <button
            type="button"
            onClick={() => setActiveTab('master')}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${
              activeTab === 'master'
                ? 'bg-blue-600 text-white shadow-sm'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            <i className="ri-list-check-2"></i>
            Master Serial List
            {totalCount > 0 && (
              <span className={`px-2 py-0.5 rounded-full text-xs ${activeTab === 'master' ? 'bg-blue-700 text-white' : 'bg-gray-200 text-gray-700'}`}>
                {totalCount}
              </span>
            )}
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('warranty')}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${
              activeTab === 'warranty'
                ? 'bg-blue-600 text-white shadow-sm'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            <i className="ri-shield-check-line"></i>
            Warranty Tracking
            <span className="px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-800 font-bold">
              Expiring Alerts
            </span>
          </button>
        </div>

        {/* TAB 1: MASTER SERIAL LIST */}
        {activeTab === 'master' && (
          <div className="space-y-4">
            {/* Filters Bar */}
            <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex flex-col md:flex-row gap-3 items-center justify-between">
              {/* Search */}
              <form onSubmit={handleSearchSubmit} className="relative w-full md:w-72">
                <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"></i>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search Serial, Vendor SN, IMEI..."
                  className="w-full pl-9 pr-4 py-2 text-xs rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                />
              </form>

              {/* Dropdowns */}
              <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
                {/* Individual Particular Item Filter Dropdown */}
                <div className="relative">
                  <select
                    value={selectedItemId}
                    onChange={(e) => {
                      setSelectedItemId(e.target.value);
                      setPage(1);
                    }}
                    className="px-3 py-2 text-xs rounded-xl border border-blue-200 bg-blue-50/50 hover:bg-blue-50 focus:ring-2 focus:ring-blue-500 font-medium text-blue-900 max-w-[200px] truncate"
                    title="Filter by particular Item"
                  >
                    <option value="ALL">📦 All Particular Items</option>
                    {itemsList.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name} {item.code ? `(${item.code})` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                <select
                  value={statusFilter}
                  onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                  className="px-3 py-2 text-xs rounded-xl border border-gray-200 bg-white focus:ring-2 focus:ring-blue-500 font-medium text-gray-700"
                >
                  <option value="ALL">All Statuses</option>
                  <option value="IN_STOCK">IN STOCK</option>
                  <option value="SOLD">SOLD</option>
                  <option value="RETURNED">RETURNED</option>
                  <option value="DEFECTIVE">DEFECTIVE</option>
                  <option value="IN_REPAIR">IN REPAIR</option>
                  <option value="ON_HOLD">ON HOLD</option>
                  <option value="SCRAPPED">SCRAPPED</option>
                </select>

                <select
                  value={conditionFilter}
                  onChange={(e) => { setConditionFilter(e.target.value); setPage(1); }}
                  className="px-3 py-2 text-xs rounded-xl border border-gray-200 bg-white focus:ring-2 focus:ring-blue-500 font-medium text-gray-700"
                >
                  <option value="ALL">All Conditions</option>
                  <option value="GOOD">GOOD</option>
                  <option value="DAMAGED_BOX">DAMAGED BOX</option>
                  <option value="DEFECTIVE">DEFECTIVE</option>
                  <option value="REFURBISHED">REFURBISHED</option>
                </select>

                <button
                  type="button"
                  onClick={() => {
                    setSearch('');
                    setSelectedItemId('ALL');
                    setStatusFilter('ALL');
                    setConditionFilter('ALL');
                    setPage(1);
                  }}
                  className="px-3 py-2 text-xs text-gray-600 hover:text-gray-900 bg-gray-50 border border-gray-200 rounded-xl"
                  title="Reset all filters"
                >
                  Reset
                </button>
              </div>

              {/* View Mode Toggle: Grouped Dropdown vs Flat List */}
              <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-xl self-end md:self-auto">
                <button
                  type="button"
                  onClick={() => setViewMode('grouped')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all ${
                    viewMode === 'grouped'
                      ? 'bg-white text-blue-700 shadow-xs'
                      : 'text-gray-500 hover:text-gray-800'
                  }`}
                  title="Group serials under each individual item accordion"
                >
                  <i className="ri-folders-line"></i> Grouped by Item
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('flat')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all ${
                    viewMode === 'flat'
                      ? 'bg-white text-blue-700 shadow-xs'
                      : 'text-gray-500 hover:text-gray-800'
                  }`}
                  title="View all serials in a flat table"
                >
                  <i className="ri-table-line"></i> Flat List
                </button>
              </div>
            </div>

            {/* Quick Actions Bar for Grouped Mode */}
            {viewMode === 'grouped' && groupedSerials.length > 0 && !loading && (
              <div className="flex items-center justify-between px-2 text-xs text-gray-500">
                <span>
                  Showing <strong>{groupedSerials.length}</strong> {groupedSerials.length === 1 ? 'item' : 'items'} with serial inventory
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => toggleAll(true)}
                    className="hover:text-blue-600 font-medium underline"
                  >
                    Expand All Dropdowns
                  </button>
                  <span>•</span>
                  <button
                    type="button"
                    onClick={() => toggleAll(false)}
                    className="hover:text-blue-600 font-medium underline"
                  >
                    Collapse All
                  </button>
                </div>
              </div>
            )}

            {/* Loading / Empty State */}
            {loading ? (
              <div className="bg-white p-12 rounded-2xl shadow-sm border border-gray-100 text-center text-gray-400">
                <i className="ri-loader-4-line text-3xl animate-spin block mb-2 text-blue-600 mx-auto"></i>
                <span className="text-sm font-medium">Loading serial numbers...</span>
              </div>
            ) : serials.length === 0 ? (
              <div className="bg-white p-12 rounded-2xl shadow-sm border border-gray-100 text-center text-gray-400">
                <i className="ri-qr-code-line text-4xl block mb-2 opacity-40 mx-auto"></i>
                <p className="text-sm font-medium text-gray-600">No serial numbers found matching your criteria.</p>
                <p className="text-xs text-gray-400 mt-1">Try resetting your item, status, or search filters.</p>
              </div>
            ) : viewMode === 'grouped' ? (
              /* VIEW MODE 1: GROUPED BY INDIVIDUAL PARTICULAR ITEM (Collapsible Dropdowns) */
              <div className="space-y-3.5">
                {groupedSerials.map((group) => {
                  const isExpanded = !!expandedItems[group.itemId];
                  return (
                    <div
                      key={group.itemId}
                      className="border border-gray-200/80 rounded-2xl overflow-hidden bg-white shadow-sm transition-all duration-200 hover:border-blue-200"
                    >
                      {/* Collapsible Dropdown Header for Item */}
                      <div
                        onClick={() => toggleItem(group.itemId)}
                        className="px-5 py-4 bg-gradient-to-r from-gray-50/90 via-slate-50/50 to-white hover:bg-blue-50/30 cursor-pointer flex flex-col sm:flex-row sm:items-center justify-between gap-3 transition-colors border-b border-gray-100 select-none"
                      >
                        <div className="flex items-center gap-3">
                          <span className="w-10 h-10 rounded-xl bg-blue-100/80 text-blue-700 flex items-center justify-center font-bold text-lg shadow-2xs">
                            <i className="ri-box-3-line"></i>
                          </span>
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="font-bold text-gray-900 text-sm">{group.itemName}</h3>
                              {group.itemCode && (
                                <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-[11px] font-mono font-medium">
                                  {group.itemCode}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {group.total} {group.total === 1 ? 'Serial Unit' : 'Serial Units'} in record
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-3 flex-wrap">
                          {/* Badges */}
                          <div className="flex items-center gap-1.5">
                            <span className="px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                              {group.inStock} In Stock
                            </span>
                            {group.sold > 0 && (
                              <span className="px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                                {group.sold} Sold
                              </span>
                            )}
                            {group.other > 0 && (
                              <span className="px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-gray-100 text-gray-700 border border-gray-200">
                                {group.other} Other
                              </span>
                            )}
                          </div>

                          {/* Accordion / Dropdown indicator */}
                          <div className="flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-semibold text-gray-700 bg-white border border-gray-200 shadow-2xs group-hover:bg-blue-50">
                            <span>{isExpanded ? 'Hide Serials' : `Show Serials (${group.total})`}</span>
                            <i
                              className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${
                                isExpanded ? 'rotate-180 text-blue-600' : 'text-gray-400'
                              }`}
                            ></i>
                          </div>
                        </div>
                      </div>

                      {/* Dropdown Serials Table */}
                      {isExpanded && (
                        <div className="overflow-x-auto bg-white animate-in slide-in-from-top-1 duration-200">
                          <table className="w-full text-xs text-left">
                            <thead className="bg-gray-50/60 text-gray-500 font-semibold uppercase tracking-wider border-b border-gray-100">
                              <tr>
                                <th className="py-3 px-4">Serial Number</th>
                                <th className="py-3 px-4">Vendor SN / IMEI</th>
                                <th className="py-3 px-4">Status</th>
                                <th className="py-3 px-4">Condition</th>
                                <th className="py-3 px-4">Location / Customer</th>
                                <th className="py-3 px-4">Warranty Expiry</th>
                                <th className="py-3 px-4 text-center">Actions</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {group.serials.map((s) => renderSerialRow(s, false))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              /* VIEW MODE 2: FLAT TABLE VIEW */
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs text-left">
                    <thead className="bg-gray-50/80 text-gray-500 font-semibold uppercase tracking-wider border-b border-gray-100">
                      <tr>
                        <th className="py-3.5 px-4">Serial Number</th>
                        <th className="py-3.5 px-4">Vendor SN / IMEI</th>
                        <th className="py-3.5 px-4">Item Name</th>
                        <th className="py-3.5 px-4">Status</th>
                        <th className="py-3.5 px-4">Condition</th>
                        <th className="py-3.5 px-4">Location / Cust</th>
                        <th className="py-3.5 px-4">Warranty</th>
                        <th className="py-3.5 px-4 text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {serials.map((s) => renderSerialRow(s, true))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Pagination */}
            <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex items-center justify-between text-xs text-gray-500">
              <span>
                Showing {serials.length} of {totalCount} serial units (Page {page} of {totalPages})
              </span>
              <div className="flex gap-1.5">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-100 disabled:opacity-40"
                >
                  Previous
                </button>
                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className="px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-100 disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}

        {/* TAB 2: WARRANTY TRACKING */}
        {activeTab === 'warranty' && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden space-y-4 p-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
              <div>
                <h2 className="text-base font-bold text-gray-900">Serials with Expiring Warranty</h2>
                <p className="text-xs text-gray-500">Alerts for devices requiring warranty renewal, customer notification, or inspection.</p>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">Threshold:</span>
                <select
                  value={warrantyDays}
                  onChange={(e) => setWarrantyDays(Number(e.target.value))}
                  className="px-3 py-1.5 text-xs rounded-xl border border-gray-200 bg-white font-medium"
                >
                  <option value={30}>Expiring in 30 Days (Critical)</option>
                  <option value={60}>Expiring in 60 Days</option>
                  <option value={90}>Expiring in 90 Days</option>
                  <option value={180}>Expiring in 180 Days</option>
                </select>
              </div>
            </div>

            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <table className="w-full text-xs text-left">
                <thead className="bg-gray-50 text-gray-600 font-semibold border-b border-gray-200">
                  <tr>
                    <th className="py-3 px-4">Serial Number</th>
                    <th className="py-3 px-4">Item Name</th>
                    <th className="py-3 px-4">Customer</th>
                    <th className="py-3 px-4">Expiry Date</th>
                    <th className="py-3 px-4">Days Left</th>
                    <th className="py-3 px-4 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {warrantyLoading ? (
                    <tr>
                      <td colSpan={6} className="py-12 text-center text-gray-400">
                        <i className="ri-loader-4-line text-2xl animate-spin block mb-2 text-blue-600"></i>
                        Checking warranty schedules...
                      </td>
                    </tr>
                  ) : warrantySerials.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="py-12 text-center text-gray-400">
                        <i className="ri-shield-check-line text-3xl block mb-2 opacity-40 text-emerald-500"></i>
                        All clear! No serial warranties expiring within {warrantyDays} days.
                      </td>
                    </tr>
                  ) : (
                    warrantySerials.map((s) => {
                      const days = Math.round(Number(s.days_left || 0));
                      let badgeClass = 'bg-emerald-50 text-emerald-700 border-emerald-200';
                      let label = `${days} days left 🟢`;

                      if (days <= 0) {
                        badgeClass = 'bg-gray-100 text-gray-700 border-gray-300';
                        label = `Expired (${Math.abs(days)}d ago) ⚪`;
                      } else if (days <= 15) {
                        badgeClass = 'bg-rose-50 text-rose-700 border-rose-200';
                        label = `${days} days left 🔴`;
                      } else if (days <= 45) {
                        badgeClass = 'bg-amber-50 text-amber-700 border-amber-200';
                        label = `${days} days left 🟡`;
                      }

                      return (
                        <tr key={s.id} className="hover:bg-gray-50">
                          <td className="py-3 px-4 font-mono font-bold text-gray-900">{s.serial_number}</td>
                          <td className="py-3 px-4 font-medium text-gray-800">{s.item_name}</td>
                          <td className="py-3 px-4 text-gray-600">{s.customer_name || 'In Warehouse'}</td>
                          <td className="py-3 px-4 font-mono font-medium">{formatWarrantyDate(s.warranty_expiry_date)}</td>
                          <td className="py-3 px-4">
                            <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${badgeClass}`}>
                              {label}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <div className="flex items-center justify-center gap-1.5">
                              <button
                                type="button"
                                onClick={() => handleOpenExtendModal(s)}
                                className="px-2.5 py-1 text-xs bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg font-medium transition-colors"
                              >
                                Extend Warranty
                              </button>
                              <button
                                type="button"
                                onClick={() => navigate(`/inventory/serials/${s.id}`)}
                                className="px-2.5 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                              >
                                View
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* EXTEND WARRANTY MODAL */}
        {extendModalSerial && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 max-w-md w-full p-6 space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-base font-bold text-gray-900">Extend Warranty</h3>
                <button
                  type="button"
                  onClick={() => setExtendModalSerial(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <i className="ri-close-line text-lg"></i>
                </button>
              </div>

              <p className="text-xs text-gray-500">
                Serial: <span className="font-bold text-gray-800">{extendModalSerial.serial_number}</span> ({extendModalSerial.item_name})
              </p>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">New Warranty Expiry Date</label>
                <input
                  type="date"
                  value={newWarrantyDate}
                  onChange={(e) => setNewWarrantyDate(e.target.value)}
                  className="w-full px-3 py-2 text-xs rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setExtendModalSerial(null)}
                  className="px-4 py-2 text-xs border border-gray-200 rounded-xl hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveExtendedWarranty}
                  disabled={extendSaving || !newWarrantyDate}
                  className="px-4 py-2 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold disabled:opacity-50"
                >
                  {extendSaving ? 'Saving...' : 'Update Expiry'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}

